import { makeAutoObservable, runInAction } from 'mobx';
import {
  superadminListServiceCenters,
  superadminCreateServiceCenter,
  superadminUpdateServiceCenter,
  superadminDeleteServiceCenter,
  superadminListServiceCenterAdmins,
  superadminCreateServiceCenterAdmin,
  superadminDeleteServiceCenterAdmin,
} from '@pro-sto/api-client';
import type { CreateServiceCenterDto, UpdateServiceCenterDto, CreateAdminDto } from '@pro-sto/api-client';
import { toastStore } from './ToastStore';

/** Local type describing what the API returns for a service center */
export interface ServiceCenterItem {
  uuid: string;
  name: string;
  address: string;
  location: { [key: string]: unknown };
  logo_url?: { [key: string]: unknown } | null;
  isPublished: boolean;
  payment_recipient_phone?: { [key: string]: unknown } | null;
  inn?: { [key: string]: unknown } | null;
  legal_name?: { [key: string]: unknown } | null;
  bookingsCount: number;
  revenue: number;
  commission: number;
  pendingPayout: number;
  commissionRate: number;
  rating: number;
  created_at: string;
  updated_at: string;
  /** @deprecated Not returned by API — reserved for create/edit forms */
  postsCount?: number;
  [key: string]: unknown;
}

export interface AdminItem {
  uuid: string;
  phone: string;
  name?: string;
  email?: string;
}

export class PartnersStore {
  serviceCenters: ServiceCenterItem[] = [];
  isLoading = false;
  isSubmitting = false;

  /** Modal state */
  isModalOpen = false;
  editingPartner: ServiceCenterItem | null = null;
  editingAdmins: AdminItem[] = [];

  /** Delete confirmation */
  deletingUuid: string | null = null;

  constructor() {
    makeAutoObservable(this);
  }

  /** Load all service centers */
  loadServiceCenters = async () => {
    this.isLoading = true;
    const now = new Date();
    try {
      const response = await superadminListServiceCenters({
        month: now.getMonth() + 1,
        year: now.getFullYear(),
      });
      runInAction(() => {
        this.serviceCenters = (response.data as ServiceCenterItem[]) || [];
        this.isLoading = false;
      });
    } catch (error) {
      runInAction(() => {
        this.isLoading = false;
      });
      toastStore.showError('Не удалось загрузить список партнеров');
      console.error('Failed to load service centers:', error);
    }
  };

  /** Open modal for creating a new partner */
  openCreateModal = () => {
    this.editingPartner = null;
    this.editingAdmins = [];
    this.isModalOpen = true;
  };

  /** Open modal for editing an existing partner */
  openEditModal = async (partner: ServiceCenterItem) => {
    this.editingPartner = { ...partner };
    this.isModalOpen = true;
    // Load admins for this service center
    try {
      const admins = await superadminListServiceCenterAdmins({
        serviceCenterUuid: partner.uuid,
      });
      runInAction(() => {
        this.editingAdmins = (admins as AdminItem[]) || [];
      });
    } catch (error) {
      runInAction(() => {
        this.editingAdmins = [];
      });
      console.error('Failed to load admins:', error);
    }
  };

  /** Close modal */
  closeModal = () => {
    this.isModalOpen = false;
    this.editingPartner = null;
    this.editingAdmins = [];
  };

  /** Create a new service center */
  createServiceCenter = async (
    data: {
      name: string;
      address: string;
      lat?: string | number;
      lon?: string | number;
      postsCount: number;
      isPublished: number;
      admins: Array<{ phone: string }>;
    }
  ) => {
    this.isSubmitting = true;
    try {
      const latNum = data.lat !== undefined && data.lat !== '' ? parseFloat(String(data.lat)) : 0;
      const lonNum = data.lon !== undefined && data.lon !== '' ? parseFloat(String(data.lon)) : 0;
      const requestBody: CreateServiceCenterDto = {
        name: data.name,
        address: data.address,
        location: { lat: isNaN(latNum) ? 0 : latNum, lon: isNaN(lonNum) ? 0 : lonNum },
        working_hours: {
          mon: '09:00-18:00',
          tue: '09:00-18:00',
          wed: '09:00-18:00',
          thu: '09:00-18:00',
          fri: '09:00-18:00',
          sat: '10:00-16:00',
          sun: 'closed',
        },
        contacts: {},
      };

      const result = await superadminCreateServiceCenter({ requestBody });
      const newCenter = result as ServiceCenterItem;

      // Create admins for this service center
      if (data.admins.length > 0 && newCenter.uuid) {
        for (const admin of data.admins) {
          if (admin.phone.trim()) {
            try {
              const adminBody: CreateAdminDto = {
                phone: admin.phone,
                name: `Администратор`,
                email: `admin_${Date.now()}@prosto.ru`,
                password: 'TempPass123!',
              };
              await superadminCreateServiceCenterAdmin({
                serviceCenterUuid: newCenter.uuid,
                requestBody: adminBody,
              });
            } catch (err) {
              console.error('Failed to create admin:', err);
            }
          }
        }
      }

      // Update isPublished if needed
      if (data.isPublished === 1 && newCenter.uuid) {
        try {
          await superadminUpdateServiceCenter({
            uuid: newCenter.uuid,
            requestBody: { isPublished: true },
          });
        } catch (err) {
          console.error('Failed to update isPublished:', err);
        }
      }

      runInAction(() => {
        this.isSubmitting = false;
        this.closeModal();
      });

      toastStore.showSuccess('Партнер успешно создан');
      await this.loadServiceCenters();
    } catch (error) {
      runInAction(() => {
        this.isSubmitting = false;
      });
      toastStore.showError('Не удалось создать партнера');
      console.error('Failed to create service center:', error);
    }
  };

  /** Update an existing service center */
  updateServiceCenter = async (
    uuid: string,
    data: {
      name: string;
      address: string;
      lat?: string | number;
      lon?: string | number;
      postsCount: number;
      isPublished: number;
      admins: Array<{ phone: string; uuid?: string }>;
    }
  ) => {
    this.isSubmitting = true;
    try {
      const latNum = data.lat !== undefined && data.lat !== '' ? parseFloat(String(data.lat)) : undefined;
      const lonNum = data.lon !== undefined && data.lon !== '' ? parseFloat(String(data.lon)) : undefined;
      const requestBody: UpdateServiceCenterDto = {
        name: data.name,
        address: data.address,
        isPublished: Boolean(data.isPublished),
        ...(latNum !== undefined && lonNum !== undefined && !isNaN(latNum) && !isNaN(lonNum)
          ? { location: { lat: latNum, lon: lonNum } }
          : {}),
      };

      await superadminUpdateServiceCenter({ uuid, requestBody });

      // Handle admins: add new ones (those without uuid)
      for (const admin of data.admins) {
        if (!admin.uuid && admin.phone.trim()) {
          try {
            const adminBody: CreateAdminDto = {
              phone: admin.phone,
              name: `Администратор`,
              email: `admin_${Date.now()}_${Math.random().toString(36).slice(2)}@prosto.ru`,
              password: 'TempPass123!',
            };
            await superadminCreateServiceCenterAdmin({
              serviceCenterUuid: uuid,
              requestBody: adminBody,
            });
          } catch (err) {
            console.error('Failed to create admin:', err);
          }
        }
      }

      runInAction(() => {
        this.isSubmitting = false;
        this.closeModal();
      });

      toastStore.showSuccess('Партнер успешно обновлён');
      await this.loadServiceCenters();
    } catch (error) {
      runInAction(() => {
        this.isSubmitting = false;
      });
      toastStore.showError('Не удалось обновить партнера');
      console.error('Failed to update service center:', error);
    }
  };

  /** Delete a service center */
  deleteServiceCenter = async (uuid: string) => {
    try {
      await superadminDeleteServiceCenter({ uuid });
      toastStore.showSuccess('Партнер успешно удалён');
      await this.loadServiceCenters();
    } catch (error) {
      toastStore.showError('Не удалось удалить партнера');
      console.error('Failed to delete service center:', error);
    } finally {
      runInAction(() => {
        this.deletingUuid = null;
      });
    }
  };

  /** Remove admin from service center */
  removeAdmin = async (serviceCenterUuid: string, adminUuid: string) => {
    try {
      await superadminDeleteServiceCenterAdmin({
        serviceCenterUuid,
        adminUuid,
      });
      runInAction(() => {
        this.editingAdmins = this.editingAdmins.filter((a) => a.uuid !== adminUuid);
      });
    } catch (error) {
      toastStore.showError('Не удалось удалить администратора');
      console.error('Failed to delete admin:', error);
    }
  };

  /** Ask for delete confirmation */
  confirmDelete = (uuid: string) => {
    this.deletingUuid = uuid;
  };

  cancelDelete = () => {
    this.deletingUuid = null;
  };
}

export const partnersStore = new PartnersStore();
